import React, { useState, lazy, Suspense } from "react";
import { IoChevronBack, IoChevronDown } from "react-icons/io5";
import { FaPlus, FaMinus } from "react-icons/fa";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";

const SymbolSelectionScreen = lazy(
  () => import("../trade-order/components/SymbolSelectionScreen"),
);

const PositionSelectionScreen = lazy(
  () => import("./components/PositionSelectionScreen"),
);

// const ModifyOrderTypeSelection = dynamic(
//   () => import("./components/ModifyOrderTypeSelection"),
//   {
//     ssr: false,
//   }
// );

import type { ModifyOrderType } from "./components";

interface ModifyPositionScreenProps {
  position: {
    symbol: string;
    type: "buy" | "sell";
    volume: number;
    ticket?: number;
    openPrice?: number;
    stopLoss?: number;
    takeProfit?: number;
  };
  currentBid: number;
  currentAsk: number;
  onBack: () => void;
  onModify: (stopLoss: number | null, takeProfit: number | null) => void;
}

export default function ModifyPositionScreen({
  position,
  currentBid = 42271,
  currentAsk = 42275,
  onBack,
  onModify,
}: ModifyPositionScreenProps) {
  // Get subscribed symbols (non-reactive approach like in TradeOrderScreen)
  const getSubscribedSymbols = () => {
    const store = useSubscribedSymbolsStore.getState();
    return store.allSubscribed.filter((entry) => entry.quote);
  };

  // Get available symbols for selection
  const subscribedSymbols = getSubscribedSymbols();

  // Derive precision from the symbol's real digits instead of hard-coded
  // symbol-name checks ("GOLD" never matches real symbols like GOLDoz).
  const getDigitsFor = (sym: string | undefined): number => {
    if (!sym) return 5;
    const entry = useSubscribedSymbolsStore.getState().byCurrency.get(sym);
    const d = entry?.symbol?.digits;
    if (typeof d === "number" && Number.isFinite(d) && d >= 0 && d <= 16) {
      return Math.floor(d);
    }
    return sym.includes("JPY") ? 3 : 5;
  };
  const initialDigits = getDigitsFor(position.symbol);

  const [stopLoss, setStopLoss] = useState<number | null>(position.stopLoss || null);
  const [takeProfit, setTakeProfit] = useState<number | null>(position.takeProfit || null);
  const [selectedSymbol, setSelectedSymbol] = useState(position.symbol);
  const [selectedPosition, setSelectedPosition] = useState(position.ticket?.toString() || "0");
  const [showSymbolSelection, setShowSymbolSelection] = useState(false);
  const [showPositionSelection, setShowPositionSelection] = useState(false);
  const [orderType, setOrderType] =
    useState<ModifyOrderType>("Modify Position");
  const [showOrderTypeSelection, setShowOrderTypeSelection] = useState(false);

  // Simplified input states - always show inputs
  const [stopLossInput, setStopLossInput] = useState(
    position.stopLoss ? position.stopLoss.toFixed(
      initialDigits
    ) : ""
  );
  const [takeProfitInput, setTakeProfitInput] = useState(
    position.takeProfit ? position.takeProfit.toFixed(
      initialDigits
    ) : ""
  );

  // New states for pending order fields
  const [orderPrice, setOrderPrice] = useState<number | null>(null);
  const [orderPriceInput, setOrderPriceInput] = useState("");
  const [showOrderPriceInput, setShowOrderPriceInput] = useState(false);

  const [stopLimitPrice, setStopLimitPrice] = useState<number | null>(null);
  const [stopLimitPriceInput, setStopLimitPriceInput] = useState("");
  const [showStopLimitPriceInput, setShowStopLimitPriceInput] = useState(false);

  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Get current quote for selected symbol (reactive subscription)
  const currentQuote = useSubscribedSymbolsStore((state) => {
    if (!selectedSymbol) return null;
    const symbolEntry = state.byCurrency.get(selectedSymbol);
    return symbolEntry?.quote || null;
  });

  // Digits-derived price math for the selected symbol.
  const symbolDigits = useSubscribedSymbolsStore((state) => {
    const d = selectedSymbol
      ? state.byCurrency.get(selectedSymbol)?.symbol?.digits
      : undefined;
    if (typeof d === "number" && Number.isFinite(d) && d >= 0 && d <= 16) {
      return Math.floor(d);
    }
    return selectedSymbol?.includes("JPY") ? 3 : 5;
  });
  /** One pip (MT convention: 10 points) at the symbol's precision. */
  const pipSize = Math.pow(10, -(symbolDigits - 1));
  /** One tick — used as the number-input step attribute. */
  const tickStepAttr = Math.pow(10, -symbolDigits).toFixed(symbolDigits);
  /** Default SL/TP distance when seeding an empty input: 10 pips. */
  const defaultLevelDistance = 10 * pipSize;

  /**
   * Which side each level belongs on. A stop is only a stop when it sits BEHIND the position: below
   * the market for a Buy, above it for a Sell (take-profits mirror that). These used to be hard-wired
   * to `bid - d` / `ask + d` whatever the direction, so every default on a SELL was already triggered
   * the moment it was saved and the engine closed the position at market (SR Global, 2026-09-09).
   */
  const isBuyPosition = position.type === "buy";
  const defaultSlPrice = () =>
    isBuyPosition
      ? bidPrice - defaultLevelDistance
      : askPrice + defaultLevelDistance;
  const defaultTpPrice = () =>
    isBuyPosition
      ? askPrice + defaultLevelDistance
      : bidPrice - defaultLevelDistance;

  // Get real prices from quote
  const getPrices = () => {
    if (currentQuote) {
      return {
        bid: currentQuote.bid,
        ask: currentQuote.ask,
      };
    }
    // Fallback to props if no live quote available
    return { bid: currentBid, ask: currentAsk };
  };

  const { bid: bidPrice, ask: askPrice } = getPrices();

  // Get symbol description from subscribed data (like in TradeOrderScreen)
  const getSymbolDescription = (symbolCurrency: string) => {
    const symbolEntry = subscribedSymbols.find(
      (entry) => entry.symbol.currency === symbolCurrency
    );
    return symbolEntry?.symbol.description || symbolCurrency;
  };

  // Helper functions for input handling
  const handleStopLossChange = (value: string) => {
    setStopLossInput(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setStopLoss(numValue);
    } else if (value === "" && !stopLoss) {
      // Set default stop loss when input is empty and no SL set
      const defaultStopLoss = defaultSlPrice();
      setStopLoss(defaultStopLoss);
      setStopLossInput(
        defaultStopLoss.toFixed(
          symbolDigits
        )
      );
    }
  };

  const handleStopLossBlur = () => {
    const numValue = parseFloat(stopLossInput);
    if (isNaN(numValue) || numValue <= 0) {
      setStopLoss(null);
      setStopLossInput("");
    } else {
      setStopLoss(numValue);
      setStopLossInput(
        numValue.toFixed(
          symbolDigits
        )
      );
    }
  };

  const handleTakeProfitChange = (value: string) => {
    setTakeProfitInput(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setTakeProfit(numValue);
    } else if (value === "" && !takeProfit) {
      // Set default take profit when input is empty and no TP set
      const defaultTakeProfit = defaultTpPrice();
      setTakeProfit(defaultTakeProfit);
      setTakeProfitInput(
        defaultTakeProfit.toFixed(
          symbolDigits
        )
      );
    }
  };
  const handleTakeProfitBlur = () => {
    const numValue = parseFloat(takeProfitInput);
    if (isNaN(numValue) || numValue <= 0) {
      setTakeProfit(null);
      setTakeProfitInput("");
    } else {
      setTakeProfit(numValue);
      setTakeProfitInput(
        numValue.toFixed(
          symbolDigits
        )
      );
    }
  };

  const calculatePipDistance = (price1: number, price2: number) => {
    const diff = Math.abs(price1 - price2);
    return Math.round(diff / pipSize);
  };

  const handleOrderPriceChange = (value: string) => {
    setOrderPriceInput(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setOrderPrice(numValue);
    }
  };

  const handleOrderPriceBlur = () => {
    const numValue = parseFloat(orderPriceInput);
    if (isNaN(numValue) || numValue <= 0) {
      setOrderPrice(null);
      setOrderPriceInput("");
      setShowOrderPriceInput(false);
    } else {
      setOrderPrice(numValue);
      setOrderPriceInput(
        numValue.toFixed(
          symbolDigits
        )
      );
    }
  };

  const handleStopLimitPriceChange = (value: string) => {
    setStopLimitPriceInput(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setStopLimitPrice(numValue);
    }
  };

  const handleStopLimitPriceBlur = () => {
    const numValue = parseFloat(stopLimitPriceInput);
    if (isNaN(numValue) || numValue <= 0) {
      setStopLimitPrice(null);
      setStopLimitPriceInput("");
      setShowStopLimitPriceInput(false);
    } else {
      setStopLimitPrice(numValue);
      setStopLimitPriceInput(
        numValue.toFixed(
          symbolDigits
        )
      );
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const isPendingOrderType = () => {
    return orderType !== "Market Execution" && orderType !== "Modify Position";
  };

  const isStopLimitType = () => {
    return orderType === "Buy Stop Limit" || orderType === "Sell Stop Limit";
  };

  const [levelError, setLevelError] = useState<string | null>(null);

  /** MT5 parity: refuse a level on the wrong side of the market rather than sending it — ViiSync
   *  applies it and its trigger gate closes the position immediately. */
  const validateLevels = (): string | null => {
    const market = isBuyPosition ? bidPrice : askPrice;
    if (!market) return null; // no quote yet — nothing to validate against
    if (stopLoss && stopLoss > 0) {
      if (isBuyPosition && stopLoss >= market)
        return "Stop Loss for a Buy must be below the current price.";
      if (!isBuyPosition && stopLoss <= market)
        return "Stop Loss for a Sell must be above the current price.";
    }
    if (takeProfit && takeProfit > 0) {
      if (isBuyPosition && takeProfit <= market)
        return "Take Profit for a Buy must be above the current price.";
      if (!isBuyPosition && takeProfit >= market)
        return "Take Profit for a Sell must be below the current price.";
    }
    return null;
  };

  const handleModify = () => {
    const err = validateLevels();
    setLevelError(err);
    if (err) return;
    onModify(stopLoss, takeProfit);
  };

  // if (showSymbolSelection) {
  //   const availableSymbols = quotes.map(q => q.symbol).sort();

  //   return (
  //     <SymbolSelectionScreen
  //       selectedSymbol={selectedSymbol}
  //       onSelectSymbol={setSelectedSymbol}
  //       onBack={() => setShowSymbolSelection(false)}
  //       availableSymbols={availableSymbols}
  //     />
  //   );
  // }

  if (showPositionSelection) {
    return (
      <Suspense fallback={null}>
        <PositionSelectionScreen
          selectedPosition={`#${selectedPosition}`}
          onSelectPosition={(id) => setSelectedPosition(id.replace("#", ""))}
          onBack={() => setShowPositionSelection(false)}
        />
      </Suspense>
    );
  }

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header */}
      <div className="px-4 py-4">
        <button onClick={onBack} className="flex items-center gap-2 mb-3">
          <IoChevronBack size={24} className="text-blue-500" />
        </button>

        <div className="text-center">
          <button
            onClick={() => setShowSymbolSelection(true)}
            className="flex items-center justify-center gap-2 mb-1 mx-auto"
          >
            <h1 className="text-2xl font-medium">{selectedSymbol}</h1>
            {/* <IoChevronDown size={20} className="text-muted-foreground" /> */}
          </button>
          <p className="text-muted-foreground">
            {getSymbolDescription(selectedSymbol)}
          </p>
        </div>
      </div>

      {/* Order Type - Disabled */}
      <div className="px-4 mb-6">
        <div className="w-full flex items-center justify-between bg-[#1C1C1E] rounded-lg px-4 py-3 opacity-50 cursor-not-allowed">
          <span className="text-foreground text-lg">{orderType}</span>
          <IoChevronDown size={20} className="text-muted-foreground" />
        </div>
      </div>

      {/* Position Info */}
      {/* <div className="px-4 mb-6">
        <button
          onClick={() => setShowPositionSelection(true)}
          className="w-full bg-[#1C1C1E] rounded-lg px-4 py-3 flex items-center justify-between"
        >
          <span className="text-foreground text-lg">
            #{selectedPosition} {position.type} {position.volume}{" "}
            {selectedSymbol}
          </span>
          <IoChevronDown size={20} className="text-muted-foreground" />
        </button>
      </div> */}

      {/* Order Price (for pending orders) */}
      {isPendingOrderType() && (
        <div className="px-4 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-foreground text-lg">Price</span>
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  if (orderPrice && orderPrice > 0) {
                    const decrement =
                      pipSize;
                    const newValue = Math.max(0, orderPrice - decrement);
                    setOrderPrice(newValue > 0 ? newValue : null);
                    setOrderPriceInput(
                      newValue > 0
                        ? newValue.toFixed(
                            symbolDigits
                          )
                        : ""
                    );
                    if (newValue === 0) setShowOrderPriceInput(false);
                  }
                }}
                className="text-red-500 text-3xl"
              >
                <FaMinus size={20} />
              </button>
              {showOrderPriceInput ? (
                <input
                  type="number"
                  value={orderPriceInput}
                  onChange={(e) => handleOrderPriceChange(e.target.value)}
                  onBlur={handleOrderPriceBlur}
                  placeholder="Enter price"
                  className="text-foreground text-lg bg-[#1C1C1E] rounded px-2 py-1 w-32 text-center"
                  step={
                    tickStepAttr
                  }
                />
              ) : (
                <button
                  onClick={() => {
                    setShowOrderPriceInput(true);
                    setOrderPriceInput(
                      orderPrice
                        ? orderPrice.toFixed(
                            symbolDigits
                          )
                        : ""
                    );
                  }}
                  className="text-muted-foreground text-lg"
                >
                  {orderPrice
                    ? orderPrice.toFixed(
                        symbolDigits
                      )
                    : "not set"}
                </button>
              )}
              <button
                onClick={() => {
                  const increment =
                    pipSize;
                  const defaultPrice = orderType.includes("Buy")
                    ? askPrice
                    : bidPrice;
                  const newValue = (orderPrice || defaultPrice) + increment;
                  setOrderPrice(newValue);
                  setOrderPriceInput(
                    newValue.toFixed(
                      symbolDigits
                    )
                  );
                  if (showOrderPriceInput) setShowOrderPriceInput(false);
                }}
                className="text-blue-500 text-3xl"
              >
                <FaPlus size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stop Limit Price (for stop limit orders) */}
      {isStopLimitType() && (
        <div className="px-4 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-foreground text-lg">Stop Limit Price</span>
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  if (stopLimitPrice && stopLimitPrice > 0) {
                    const decrement =
                      pipSize;
                    const newValue = Math.max(0, stopLimitPrice - decrement);
                    setStopLimitPrice(newValue > 0 ? newValue : null);
                    setStopLimitPriceInput(
                      newValue > 0
                        ? newValue.toFixed(
                            symbolDigits
                          )
                        : ""
                    );
                    if (newValue === 0) setShowStopLimitPriceInput(false);
                  }
                }}
                className="text-red-500 text-3xl"
              >
                <FaMinus size={20} />
              </button>
              {showStopLimitPriceInput ? (
                <input
                  type="number"
                  value={stopLimitPriceInput}
                  onChange={(e) => handleStopLimitPriceChange(e.target.value)}
                  onBlur={handleStopLimitPriceBlur}
                  placeholder="Enter price"
                  className="text-foreground text-lg bg-[#1C1C1E] rounded px-2 py-1 w-32 text-center"
                  step={
                    tickStepAttr
                  }
                />
              ) : (
                <button
                  onClick={() => {
                    setShowStopLimitPriceInput(true);
                    setStopLimitPriceInput(
                      stopLimitPrice
                        ? stopLimitPrice.toFixed(
                            symbolDigits
                          )
                        : ""
                    );
                  }}
                  className="text-muted-foreground text-lg"
                >
                  {stopLimitPrice
                    ? stopLimitPrice.toFixed(
                        symbolDigits
                      )
                    : "not set"}
                </button>
              )}
              <button
                onClick={() => {
                  const increment =
                    pipSize;
                  const defaultPrice = orderType.includes("Buy")
                    ? askPrice
                    : bidPrice;
                  const newValue = (stopLimitPrice || defaultPrice) + increment;
                  setStopLimitPrice(newValue);
                  setStopLimitPriceInput(
                    newValue.toFixed(
                      symbolDigits
                    )
                  );
                  if (showStopLimitPriceInput)
                    setShowStopLimitPriceInput(false);
                }}
                className="text-blue-500 text-3xl"
              >
                <FaPlus size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stop Loss */}
      <div className="px-4 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-foreground text-lg">Stop Loss</span>
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                if (!stopLoss) {
                  const defaultStopLoss = defaultSlPrice();
                  setStopLoss(defaultStopLoss);
                  setStopLossInput(
                    defaultStopLoss.toFixed(
                      symbolDigits
                    )
                  );
                } else if (stopLoss > 0) {
                  const decrement =
                    pipSize;
                  const newValue = Math.max(0, stopLoss - decrement);
                  setStopLoss(newValue > 0 ? newValue : null);
                  setStopLossInput(
                    newValue > 0
                      ? newValue.toFixed(
                          symbolDigits
                        )
                      : ""
                  );
                }
              }}
              className="text-red-500 text-3xl"
            >
              <FaMinus size={20} />
            </button>
            
            <input
              type="number"
              value={stopLossInput}
              onChange={(e) => handleStopLossChange(e.target.value)}
              onBlur={handleStopLossBlur}
              onFocus={() => {
                if (!stopLoss) {
                  const defaultStopLoss = defaultSlPrice();
                  setStopLoss(defaultStopLoss);
                  setStopLossInput(
                    defaultStopLoss.toFixed(
                      symbolDigits
                    )
                  );
                }
              }}
              placeholder="Enter price"
              className="text-foreground text-lg bg-[#1C1C1E] rounded px-2 py-1 w-32 text-center"
              step={
                tickStepAttr
              }
            />
            
            <button
              onClick={() => {
                if (!stopLoss) {
                  const defaultStopLoss = defaultSlPrice();
                  setStopLoss(defaultStopLoss);
                  setStopLossInput(
                    defaultStopLoss.toFixed(
                      symbolDigits
                    )
                  );
                } else {
                  const increment =
                    pipSize;
                  const newValue = stopLoss + increment;
                  setStopLoss(newValue);
                  setStopLossInput(
                    newValue.toFixed(
                      symbolDigits
                    )
                  );
                }
              }}
              className="text-blue-500 text-3xl"
            >
              <FaPlus size={20} />
            </button>
          </div>
        </div>
        {stopLoss !== null && stopLoss > 0 && (
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={() => {
                setStopLoss(null);
                setStopLossInput("");
              }}
              className="text-sm text-red-400 hover:text-red-300"
            >
              Remove stop loss
            </button>
          </div>
        )}
      </div>

      {/* Take Profit */}
      <div className="px-4 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-foreground text-lg">Take Profit</span>
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                if (!takeProfit) {
                  const defaultTakeProfit = defaultTpPrice();
                  setTakeProfit(defaultTakeProfit);
                  setTakeProfitInput(
                    defaultTakeProfit.toFixed(
                      symbolDigits
                    )
                  );
                } else if (takeProfit > 0) {
                  const decrement =
                    pipSize;
                  const newValue = Math.max(0, takeProfit - decrement);
                  setTakeProfit(newValue > 0 ? newValue : null);
                  setTakeProfitInput(
                    newValue > 0
                      ? newValue.toFixed(
                          symbolDigits
                        )
                      : ""
                  );
                }
              }}
              className="text-red-500 text-3xl"
            >
              <FaMinus size={20} />
            </button>
            
            <input
              type="number"
              value={takeProfitInput}
              onChange={(e) => handleTakeProfitChange(e.target.value)}
              onBlur={handleTakeProfitBlur}
              onFocus={() => {
                if (!takeProfit) {
                  const defaultTakeProfit = defaultTpPrice();
                  setTakeProfit(defaultTakeProfit);
                  setTakeProfitInput(
                    defaultTakeProfit.toFixed(
                      symbolDigits
                    )
                  );
                }
              }}
              placeholder="Enter price"
              className="text-foreground text-lg bg-[#1C1C1E] rounded px-2 py-1 w-32 text-center"
              step={
                tickStepAttr
              }
            />
            <button
              onClick={() => {
                if (!takeProfit) {
                  const defaultTakeProfit = defaultTpPrice();
                  setTakeProfit(defaultTakeProfit);
                  setTakeProfitInput(
                    defaultTakeProfit.toFixed(
                      symbolDigits
                    )
                  );
                } else {
                  const increment =
                    pipSize;
                  const newValue = takeProfit + increment;
                  setTakeProfit(newValue);
                  setTakeProfitInput(
                    newValue.toFixed(
                      symbolDigits
                    )
                  );
                }
              }}
              className="text-blue-500 text-3xl"
            >
              <FaPlus size={20} />
            </button>
          </div>
        </div>
        {takeProfit !== null && takeProfit > 0 && (
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={() => {
                setTakeProfit(null);
                setTakeProfitInput("");
              }}
              className="text-sm text-red-400 hover:text-red-300"
            >
              Remove take profit
            </button>
          </div>
        )}
      </div>

      {/* Expiration (for pending orders) */}
      {isPendingOrderType() && (
        <div className="px-4 mb-8">
          <button
            onClick={() => setShowDatePicker(true)}
            className="w-full flex items-center justify-between bg-[#1C1C1E] rounded-lg px-4 py-3"
          >
            <span className="text-foreground text-lg">Expiration</span>
            <span className="text-muted-foreground text-lg">
              {expirationDate ? formatDate(expirationDate) : "not set"}
            </span>
          </button>
        </div>
      )}

      {/* Price Display */}
      <div className="px-4 mb-6">
        {!currentQuote ? (
          <div className="text-center text-muted-foreground py-4">
            <p>No live quotes available for {selectedSymbol}</p>
            <p className="text-sm">Using fallback prices</p>
          </div>
        ) : (
          <div className="flex gap-2">
            <div className="flex-1 text-center">
              <div className="text-5xl font-light text-foreground">
                {symbolDigits <= 3 ? (
                  <>
                    {bidPrice.toFixed(3).split(".")[0]}
                    <sup className="text-3xl">
                      {bidPrice.toFixed(3).split(".")[1]}
                    </sup>
                  </>
                ) : (
                  <>
                    {bidPrice.toFixed(5).split(".")[0]}.
                    {bidPrice.toFixed(5).split(".")[1].slice(0, 2)}
                    <sup className="text-3xl">
                      {bidPrice.toFixed(5).split(".")[1].slice(2)}
                    </sup>
                  </>
                )}
              </div>
              <div className="text-sm text-muted-foreground mt-1">BID</div>
            </div>
            <div className="flex-1 text-center">
              <div className="text-5xl font-light text-foreground">
                {symbolDigits <= 3 ? (
                  <>
                    {askPrice.toFixed(3).split(".")[0]}
                    <sup className="text-3xl">
                      {askPrice.toFixed(3).split(".")[1]}
                    </sup>
                  </>
                ) : (
                  <>
                    {askPrice.toFixed(5).split(".")[0]}.
                    {askPrice.toFixed(5).split(".")[1].slice(0, 2)}
                    <sup className="text-3xl">
                      {askPrice.toFixed(5).split(".")[1].slice(2)}
                    </sup>
                  </>
                )}
              </div>
              <div className="text-sm text-muted-foreground mt-1">ASK</div>
            </div>
          </div>
        )}
      </div>

      {levelError && (
        <div className="px-4 mb-3">
          <p className="text-sm text-red-400 text-center">{levelError}</p>
        </div>
      )}

      {/* Modify Button */}
      <div className="px-4 mb-6">
        <button
          onClick={handleModify}
          className="w-full bg-gray-700 py-4 text-foreground text-xl font-medium rounded-lg"
        >
          Modify
        </button>
      </div>
      {/* Warning Message */}
      <div className="px-4 flex-1">
        <p className="text-muted-foreground text-center">
          Stop Loss and Take Profit levels are validated by the broker against
          the current market price and spread. Stops processing is performed on
          the broker side.
        </p>
      </div>

      {/* Order Type Selection Modal - Disabled */}
      {/* {showOrderTypeSelection && (
        <ModifyOrderTypeSelection
          selectedType={orderType}
          onSelectType={setOrderType}
          onClose={() => setShowOrderTypeSelection(false)}
        />
      )} */}

      {/* Date Picker Modal */}
      {showDatePicker && (
        <>
          <div
            className="fixed inset-0 bg-background/80 z-50"
            onClick={() => setShowDatePicker(false)}
          />
          <div className="fixed inset-x-0 bottom-0 z-50 bg-[#1C1C1E] rounded-t-xl p-4">
            <h3 className="text-foreground text-lg font-medium mb-4">
              Select Expiration Date
            </h3>
            <input
              type="date"
              min={new Date().toISOString().split("T")[0]}
              value={
                expirationDate ? expirationDate.toISOString().split("T")[0] : ""
              }
              onChange={(e) => {
                if (e.target.value) {
                  setExpirationDate(new Date(e.target.value));
                }
              }}
              className="w-full bg-background text-foreground rounded-lg px-4 py-3 mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setExpirationDate(null);
                  setShowDatePicker(false);
                }}
                className="flex-1 bg-gray-700 py-3 text-foreground rounded-lg"
              >
                Clear
              </button>
              <button
                onClick={() => setShowDatePicker(false)}
                className="flex-1 bg-blue-600 py-3 text-foreground rounded-lg"
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
